/**
 * GraphQL Accounting Repository
 * Handles GraphQL operations for Customers, Invoices, Bank Accounts, Movements
 */

import { graphqlQuery, graphqlMutation, INVOICE_FIELDS, BANK_ACCOUNT_FIELDS } from '../database/graphqlClient';

// =============================================================================
// FRAGMENTS
// =============================================================================

const MOVEMENT_FIELDS = `
  id
  accountId
  at
  amount
  direction
  counterparty
  memo
  reconciled
  invoiceId
  categoryId
`;

// =============================================================================
// TYPES
// =============================================================================

export interface Customer {
  id: string;
  email: string;
  customerType: string;
  companyName?: string;
  vatNumber?: string;
  firstName?: string;
  lastName?: string;
  fiscalCode?: string;
  phone?: string;
  pec?: string;
  address: {
    street: string;
    city: string;
    postalCode: string;
    province: string;
    country: string;
  };
  sdiRecipientCode?: string;
  paymentTerms: string;
  notes?: string;
  isActive: boolean;
  tags: string[];
  createdAt: string;
  updatedAt: string;
}

export interface InvoiceItem {
  description: string;
  quantity: number;
  unitPrice: number;
  vatRate: number;
}

export interface Invoice {
  id: string;
  number: string;
  customerId: string;
  date: string;
  dueDate?: string;
  direction: string;
  customerName: string;
  subtotal?: number;
  vatTotal: number;
  total: number;
  status: string;
  items: InvoiceItem[];
  projectId?: string;
  projectName?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface BankAccount {
  id: string;
  name: string;
  accountType: string;
  bankName?: string;
  iban?: string;
  currency: string;
  balance: number;
  initialBalance?: number;
  accountNumber?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Movement {
  id: string;
  accountId: string;
  at: string;
  amount: number;
  direction: 'in' | 'out';
  counterparty: string;
  memo?: string;
  reconciled: boolean;
  invoiceId?: string;
  categoryId?: string;
}

// =============================================================================
// QUERIES & MUTATIONS
// =============================================================================

const CUSTOMER_FIELDS = `
  id
  email
  customerType
  companyName
  vatNumber
  firstName
  lastName
  fiscalCode
  phone
  pec
  address {
    street
    city
    postalCode
    province
    country
  }
  sdiRecipientCode
  paymentTerms
  notes
  isActive
  tags
  createdAt
  updatedAt
`;

const QUERIES = {
  customers: `query { customers { ${CUSTOMER_FIELDS} } }`,
  invoices: `query { invoices { ${INVOICE_FIELDS} } }`,
  bankAccounts: `query { bankAccounts { ${BANK_ACCOUNT_FIELDS} } }`,
  movements: `query { movements { ${MOVEMENT_FIELDS} } }`,
};

const MUTATIONS = {
  // Customers
  createCustomer: `mutation CreateCustomer($input: CustomerInput!) { createCustomer(input: $input) { ${CUSTOMER_FIELDS} } }`,
  updateCustomer: `mutation UpdateCustomer($id: ID!, $input: CustomerInput!) { updateCustomer(id: $id, input: $input) { ${CUSTOMER_FIELDS} } }`,
  deleteCustomer: `mutation DeleteCustomer($id: ID!) { deleteCustomer(id: $id) }`,
  // Invoices
  createInvoice: `mutation CreateInvoice($input: InvoiceInput!) { createInvoice(input: $input) { ${INVOICE_FIELDS} } }`,
  updateInvoice: `mutation UpdateInvoice($id: ID!, $input: InvoiceInput!) { updateInvoice(id: $id, input: $input) { ${INVOICE_FIELDS} } }`,
  deleteInvoice: `mutation DeleteInvoice($id: ID!) { deleteInvoice(id: $id) }`,
  // Bank Accounts
  createBankAccount: `mutation CreateBankAccount($input: BankAccountInput!) { createBankAccount(input: $input) { ${BANK_ACCOUNT_FIELDS} } }`,
  updateBankAccount: `mutation UpdateBankAccount($id: ID!, $input: BankAccountInput!) { updateBankAccount(id: $id, input: $input) { ${BANK_ACCOUNT_FIELDS} } }`,
  deleteBankAccount: `mutation DeleteBankAccount($id: ID!) { deleteBankAccount(id: $id) }`,
};

// =============================================================================
// REPOSITORY
// =============================================================================

export class GraphQLAccountingRepository {
  // Customers
  async listCustomers(): Promise<Customer[]> {
    const result = await graphqlQuery<{ customers: Customer[] }>(QUERIES.customers);
    return result.customers;
  }

  async createCustomer(data: Omit<Customer, 'id' | 'createdAt' | 'updatedAt'>): Promise<Customer> {
    const input = {
      email: data.email,
      customerType: data.customerType,
      companyName: data.companyName || null,
      vatNumber: data.vatNumber || null,
      firstName: data.firstName || null,
      lastName: data.lastName || null,
      fiscalCode: data.fiscalCode || null,
      phone: data.phone || null,
      pec: data.pec || null,
      address: data.address,
      sdiRecipientCode: data.sdiRecipientCode || null,
      paymentTerms: data.paymentTerms,
      notes: data.notes || null,
      tags: data.tags || [],
    };

    const result = await graphqlMutation<{ createCustomer: Customer }>(
      MUTATIONS.createCustomer,
      { input }
    );
    return result.createCustomer;
  }

  async updateCustomer(id: string, data: Partial<Omit<Customer, 'id' | 'createdAt' | 'updatedAt'>>): Promise<Customer> {
    const input = {
      email: data.email,
      customerType: data.customerType,
      companyName: data.companyName,
      vatNumber: data.vatNumber,
      firstName: data.firstName,
      lastName: data.lastName,
      fiscalCode: data.fiscalCode,
      phone: data.phone,
      pec: data.pec,
      address: data.address,
      sdiRecipientCode: data.sdiRecipientCode,
      paymentTerms: data.paymentTerms,
      notes: data.notes,
      tags: data.tags,
      isActive: data.isActive,
    };

    // Remove undefined values
    Object.keys(input).forEach(key => {
      if ((input as Record<string, unknown>)[key] === undefined) {
        delete (input as Record<string, unknown>)[key];
      }
    });

    const result = await graphqlMutation<{ updateCustomer: Customer }>(
      MUTATIONS.updateCustomer,
      { id, input }
    );
    return result.updateCustomer;
  }

  async deleteCustomer(id: string): Promise<void> {
    await graphqlMutation(MUTATIONS.deleteCustomer, { id });
  }

  async getCustomer(id: string): Promise<Customer | null> {
    const customers = await this.listCustomers();
    return customers.find(c => c.id === id) || null;
  }

  // Invoices
  async listInvoices(): Promise<Invoice[]> {
    const result = await graphqlQuery<{ invoices: Invoice[] }>(QUERIES.invoices);
    return result.invoices;
  }

  async createInvoice(data: Omit<Invoice, 'id' | 'createdAt' | 'updatedAt'>): Promise<Invoice> {
    const input = {
      number: data.number,
      customerId: data.customerId,
      date: data.date,
      dueDate: data.dueDate || null,
      direction: data.direction,
      customerName: data.customerName,
      subtotal: data.subtotal,
      vatTotal: data.vatTotal,
      total: data.total,
      status: data.status,
      items: typeof data.items === 'string' ? JSON.parse(data.items) : data.items,
      projectId: data.projectId || null,
      projectName: data.projectName || null,
      notes: data.notes || null,
    };

    const result = await graphqlMutation<{ createInvoice: Invoice }>(
      MUTATIONS.createInvoice,
      { input }
    );
    return result.createInvoice;
  }

  async updateInvoice(id: string, data: Partial<Omit<Invoice, 'id' | 'createdAt' | 'updatedAt'>>): Promise<Invoice> {
    const input: Record<string, unknown> = {};

    if (data.number !== undefined) input.number = data.number;
    if (data.customerId !== undefined) input.customerId = data.customerId;
    if (data.date !== undefined) input.date = data.date;
    if (data.dueDate !== undefined) input.dueDate = data.dueDate;
    if (data.direction !== undefined) input.direction = data.direction;
    if (data.customerName !== undefined) input.customerName = data.customerName;
    if (data.subtotal !== undefined) input.subtotal = data.subtotal;
    if (data.vatTotal !== undefined) input.vatTotal = data.vatTotal;
    if (data.total !== undefined) input.total = data.total;
    if (data.status !== undefined) input.status = data.status;
    if (data.items !== undefined) input.items = typeof data.items === 'string' ? JSON.parse(data.items) : data.items;
    if (data.projectId !== undefined) input.projectId = data.projectId;
    if (data.projectName !== undefined) input.projectName = data.projectName;
    if (data.notes !== undefined) input.notes = data.notes;

    const result = await graphqlMutation<{ updateInvoice: Invoice }>(
      MUTATIONS.updateInvoice,
      { id, input }
    );
    return result.updateInvoice;
  }

  async deleteInvoice(id: string): Promise<void> {
    await graphqlMutation(MUTATIONS.deleteInvoice, { id });
  }

  async getInvoice(id: string): Promise<Invoice | null> {
    const invoices = await this.listInvoices();
    return invoices.find(i => i.id === id) || null;
  }

  // Movements
  async listMovements(): Promise<Movement[]> {
    const result = await graphqlQuery<{ movements: Movement[] }>(QUERIES.movements);
    return result.movements;
  }

  async reconcileMovement(id: string, invoiceId?: string): Promise<Movement> {
    const result = await graphqlMutation<{ reconcileMovement: Movement }>(
      `mutation ReconcileMovement($id: ID!, $invoiceId: ID) {
        reconcileMovement(id: $id, invoiceId: $invoiceId) { ${MOVEMENT_FIELDS} }
      }`,
      { id, invoiceId: invoiceId ?? null }
    );
    return result.reconcileMovement;
  }

  // Bank Accounts
  async listBankAccounts(): Promise<BankAccount[]> {
    const result = await graphqlQuery<{ bankAccounts: BankAccount[] }>(QUERIES.bankAccounts);
    return result.bankAccounts;
  }

  async createBankAccount(data: Omit<BankAccount, 'id' | 'createdAt' | 'updatedAt'>): Promise<BankAccount> {
    const input = {
      name: data.name,
      accountType: data.accountType,
      bankName: data.bankName || null,
      iban: data.iban || null,
      currency: data.currency,
      balance: data.balance,
      initialBalance: data.initialBalance || null,
      accountNumber: data.accountNumber || null,
    };

    const result = await graphqlMutation<{ createBankAccount: BankAccount }>(
      MUTATIONS.createBankAccount,
      { input }
    );
    return result.createBankAccount;
  }

  async updateBankAccount(id: string, data: Partial<Omit<BankAccount, 'id' | 'createdAt' | 'updatedAt'>>): Promise<BankAccount> {
    const input: Record<string, unknown> = {};

    if (data.name !== undefined) input.name = data.name;
    if (data.accountType !== undefined) input.accountType = data.accountType;
    if (data.bankName !== undefined) input.bankName = data.bankName;
    if (data.iban !== undefined) input.iban = data.iban;
    if (data.currency !== undefined) input.currency = data.currency;
    if (data.balance !== undefined) input.balance = data.balance;
    if (data.initialBalance !== undefined) input.initialBalance = data.initialBalance;
    if (data.accountNumber !== undefined) input.accountNumber = data.accountNumber;
    if (data.isActive !== undefined) input.isActive = data.isActive;

    const result = await graphqlMutation<{ updateBankAccount: BankAccount }>(
      MUTATIONS.updateBankAccount,
      { id, input }
    );
    return result.updateBankAccount;
  }

  async deleteBankAccount(id: string): Promise<void> {
    await graphqlMutation(MUTATIONS.deleteBankAccount, { id });
  }

  async getBankAccount(id: string): Promise<BankAccount | null> {
    const accounts = await this.listBankAccounts();
    return accounts.find(a => a.id === id) || null;
  }
}

export const graphqlAccountingRepository = new GraphQLAccountingRepository();
